// @flow
import * as segment from './segment';

import { analytics } from '../firebase';
import { users } from '../services/api/ava';
import {
  getConversationCloseReason,
  getConversationTimeInSeconds,
  getMetricsToUpdate,
  getRatingMetricsToUpdate,
  isFirstConvo,
  isHost,
  isScribeRequester,
  updateConversationMetrics,
} from '../services/conversation-metrics';

export const getBlocs = ({ speakers, transcripts }: { speakers: Object, transcripts: Object }) => {
  const conversationBlocs = [];

  Object.keys(transcripts).forEach(transcriptId => {
    const currentTranscript = transcripts[transcriptId];

    const bloc = {};
    bloc.asr = {
      final: currentTranscript['asr.final'],
      timeReceivedMs: currentTranscript['asr.timeReceivedMs'],
    };
    bloc.blocId = currentTranscript.id;
    bloc.environment = 'production';
    bloc.final = currentTranscript.final;
    bloc.id = currentTranscript.id;
    bloc.lastHumanIndexes = currentTranscript.indexes;
    bloc.speaker = speakers[currentTranscript.author].avaName;
    bloc.speakerId = currentTranscript.author;
    bloc.speechLang = currentTranscript.speechLang;
    bloc.text = currentTranscript.texts;
    bloc.usePaidASR = currentTranscript.usePaidASR;

    conversationBlocs.push(bloc);
  });

  return conversationBlocs;
};

export const getIsAllowedAccess = ({ status, user }: { status: Object, user: Object }) => {
  let isAllowedAccess = status.confidential.allowTranscriptsSaving;

  const hasSaveTranscript = !!user?.features?.saveTranscript;
  const hasForbidConversationSaving = !!user?.features?.['forbid-conversation-saving'];

  const { id: roomId, host } = status;

  if (user && (hasForbidConversationSaving || !hasSaveTranscript)) {
    isAllowedAccess = false;
  } else if (roomId.indexOf(host.avaId) !== 0) {
    if (host.flags['forbid-conversation-sharing']) {
      isAllowedAccess = false;
    } else {
      isAllowedAccess = isAllowedAccess || host.flags.saveTranscript;
    }
  }

  return isAllowedAccess;
};

export const getSpeakers = ({ speakers }: { speakers: Object }) => {
  const speakersArr = [];

  Object.keys(speakers).forEach(speakerId => {
    const currentSpeaker = speakers[speakerId];

    const speaker = {};
    speaker.avaId = speakerId;
    speaker.avaName = currentSpeaker.avaName;
    speaker.theme = currentSpeaker.theme;
    speaker.userName = currentSpeaker.userName;
    speaker.userAvatar = currentSpeaker.userAvatar;

    speakersArr.push(speaker);
  });

  return speakersArr;
};

export const updateAndFetchMetrics = async ({
  fetchAvaProfileMetrics,
  metricsToUpdate,
  user,
}: {
  fetchAvaProfileMetrics: Function,
  metricsToUpdate: Object,
  user: Object,
}) => {
  // We want to let the service worker do the long
  // running queries, especially if we're closing the window!
  if (navigator?.serviceWorker?.controller) {
    // $FlowFixMe
    navigator.serviceWorker.controller.postMessage({
      type: 'updateConvoMetrics',
      user,
      metricsToUpdate,
    });
  } else {
    // If the user is closing the window, this will not work
    // as it's a promise, long running query
    await updateConversationMetrics({ metricsToUpdate, user });

    // Now that update is done we need to fetch it again
    const updatedProfile = await users.getProfile({ user });

    const updatedMetrics = updatedProfile.data.user?.convoMetrics;
    if (updatedMetrics) {
      fetchAvaProfileMetrics(updatedMetrics);
    }
  }
};

export const trackHadConversation = async ({
  allMicsSelected,
  conversationStatus,
  conversationBroadcastedToZoom,
  endedByCurrentUser,
  endedDueToConvoRestrictedSession,
  fetchAvaProfileMetrics,
  isTranscriptSaved,
  inTwilioCalls,
  transcriptStats,
  user,
}: {
  allMicsSelected: string[],
  conversationStatus: Object,
  conversationBroadcastedToZoom: boolean,
  endedByCurrentUser: boolean,
  endedDueToConvoRestrictedSession: boolean,
  fetchAvaProfileMetrics: Function,
  isTranscriptSaved: boolean,
  inTwilioCalls: boolean,
  transcriptStats: Object,
  user: Object,
}) => {
  const { conversationMode, host, id: convoChannel, scribe, speakers, startTimestampMs } = conversationStatus;
  const { wordsTypedYou, wordsTypedGroup, wordsCapturedYou, wordsCapturedGroup } = transcriptStats;

  const conversationTimeInSeconds = getConversationTimeInSeconds(startTimestampMs);

  const currentMetrics = user.profile?.feedbackMetrics;
  const closeReason = getConversationCloseReason({
    currentUser: user,
    endedByCurrentUser,
    endedDueToConvoRestrictedSession,
    host,
  });
  const metricsToUpdate = getMetricsToUpdate({
    conversationTimeInSeconds,
    isSoloConvo: speakers?.length === 1,
    startTimestampMs: startTimestampMs,
    transcriptSaved: isTranscriptSaved,
    transcriptStats,
  });

  if (isFirstConvo(currentMetrics)) {
    segment.identifyWithId(user.avaId, { First_convo_at: new Date() });
  }

  segment.track('Had Conversation', {
    'Convo Time': conversationTimeInSeconds,
    'Convo Channel': convoChannel,
    IsFirstConvo: isFirstConvo(currentMetrics),
    IsHost: isHost({ host, user }),
    Scribe: !!scribe,
    IsTempUser: user.accountType === 'Temporary',
    IsScribeRequester: isScribeRequester({ scribe, user }),
    'Close Reason': closeReason,
    'Conversation Mode': conversationMode,
    'Participant Count': speakers.length,
    'Participant List': speakers.map(speaker => `${speaker.userName} / ${speaker.avaId} / ${speaker.avaName}`),
    IsBroadcasted: conversationBroadcastedToZoom,
    IsDialIn: inTwilioCalls,
    TranscriptSaved: isTranscriptSaved,
    'Words Typed You': wordsTypedYou,
    'Words Typed Group': wordsTypedGroup,
    'Words Captured You': wordsCapturedYou,
    'Words Captured Group': wordsCapturedGroup,
    microphone_selected: allMicsSelected,
  });
  analytics.logEvent('app_had_conversation', {
    words_captured_group: wordsCapturedGroup,
    words_captured_you: wordsCapturedYou,
    participant_count: speakers.length,
    convo_scribed: !!scribe,
    is_scribe_requester: isScribeRequester({ scribe, user }),
    is_host: isHost({ host, user }),
    convo_time: conversationTimeInSeconds,
  });

  updateAndFetchMetrics({
    fetchAvaProfileMetrics,
    metricsToUpdate,
    user,
  });
};

export const trackConversationRating = async ({
  fetchAvaProfileMetrics,
  rate,
  status,
  user,
}: {
  fetchAvaProfileMetrics: Function,
  rate?: number,
  status: Object,
  user: Object,
}) => {
  const { id: convoChannel } = status;
  const currentMetrics = user.profile?.feedbackMetrics;
  const metricsToUpdate = getRatingMetricsToUpdate({ rate });

  segment.track('Rated', {
    convo_channel: convoChannel,
    rating: rate || 0,
    is_first_5_star: rate && rate === 5 && !currentMetrics?.Rated_5,
  });

  updateAndFetchMetrics({
    fetchAvaProfileMetrics,
    metricsToUpdate,
    user,
  });
};

export const getCCModeDimensions = () => {
  const ccModeWidth = parseInt(localStorage.getItem('ccModeWidth'));
  const ccModeHeight = parseInt(localStorage.getItem('ccModeHeight'));
  return {
    width: !isNaN(ccModeWidth) ? ccModeWidth : undefined,
    height: !isNaN(ccModeHeight) ? ccModeHeight : undefined,
  };
};

export const getCCModePosition = () => {
  const ccModePositionX = parseInt(localStorage.getItem('ccModePositionX'));
  const ccModePositionY = parseInt(localStorage.getItem('ccModePositionY'));
  return {
    x: !isNaN(ccModePositionX) ? ccModePositionX : undefined,
    y: !isNaN(ccModePositionY) ? ccModePositionY : undefined,
  };
};
